/**
 * Assembles the Orientation Editor view-model from the minetwin subscriptions.
 *
 * Read-only projection of `visible_orientations`, `visible_orientation_steps`,
 * `visible_orientation_step_links`, `visible_script_actions` and the two check
 * views into the nested {@link EditorModel}. Re-derives on any subscription
 * tick; the editor keeps a local working copy seeded from this (so unsaved
 * edits survive ticks).
 *
 * `visible_orientation_check_keys` is admin-scoped (not catalog-scoped like the
 * checks themselves), so a Member-role session reads checks with no correct
 * option marked. That is the module's deliberate answer-key guard, not a
 * decode bug.
 */
import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import {
  checkKeyId,
  groupChecks,
  groupScripts,
  type EditorModel,
  type EditorOrientation,
  type EditorStep,
  type OrientationCheckRow,
  type ScriptActionRow,
} from './model';

export function useEditorModel(): {
  model: EditorModel;
  isReady: boolean;
  checksReady: boolean;
  orientationsReady: boolean;
  actionsReady: boolean;
} {
  const [orientationRows, oReady] = useTable('minetwin', minetwinTables.visible_orientations);
  const [stepRows, sReady] = useTable('minetwin', minetwinTables.visible_orientation_steps);
  const [linkRows, lReady] = useTable('minetwin', minetwinTables.visible_orientation_step_links);
  const [actionRows, aReady] = useTable('minetwin', minetwinTables.visible_script_actions);
  const [checkRows, cReady] = useTable('minetwin', minetwinTables.visible_orientation_checks);
  const [checkKeyRows, kReady] = useTable('minetwin', minetwinTables.visible_orientation_check_keys);

  const model = useMemo<EditorModel>(() => {
    // Actions grouped by stepId; `groupScripts` then keys each step's rows by
    // whatever version tag they carry (legacy timed slots included).
    const byStep = new Map<string, ScriptActionRow[]>();
    for (const a of actionRows) {
      const list = byStep.get(a.stepId) ?? [];
      list.push(a as never);
      byStep.set(a.stepId, list);
    }

    const checksByStep = new Map<string, OrientationCheckRow[]>();
    for (const c of checkRows) {
      const list = checksByStep.get(c.stepId) ?? [];
      list.push(c as never);
      checksByStep.set(c.stepId, list);
    }
    const keysByCheck = new Map<string, string[]>(
      checkKeyRows.map((k) => [checkKeyId(k.projectId, k.checkId), k.correctOptionIds]),
    );

    const steps = new Map<string, EditorStep>();
    for (const s of stepRows) {
      const scripts = groupScripts(byStep.get(s.stepId) ?? []);
      steps.set(s.stepId, {
        stepId: s.stepId,
        projectId: s.projectId,
        name: s.displayName,
        lat: s.location.latitude,
        lon: s.location.longitude,
        el: s.location.elevation,
        durationMinutes: s.durationMinutes,
        scripts,
        checks: groupChecks(checksByStep.get(s.stepId) ?? [], keysByCheck),
      });
    }

    const links = new Map<string, string[]>();
    const positions = new Map<string, Map<string, number>>();
    // usage: how many orientations link each step (drives reuse badge + lifecycle).
    const usage = new Map<string, number>();
    for (const link of linkRows) {
      const list = links.get(link.orientationId) ?? [];
      list.push(link.stepId);
      links.set(link.orientationId, list);
      const pmap = positions.get(link.orientationId) ?? new Map();
      pmap.set(link.stepId, link.position);
      positions.set(link.orientationId, pmap);
      usage.set(link.stepId, (usage.get(link.stepId) ?? 0) + 1);
    }
    for (const [oid, list] of links) {
      const pmap = positions.get(oid)!;
      list.sort((a, b) => (pmap.get(a) ?? 0) - (pmap.get(b) ?? 0));
    }

    const orientations: EditorOrientation[] = orientationRows
      .map((o) => ({
        orientationId: o.orientationId,
        projectId: o.projectId,
        displayName: o.displayName,
        occupationTarget: o.occupationTarget,
        description: o.description,
        status: o.status.tag as EditorOrientation['status'],
        autoAssign: o.autoAssign,
        passingScorePct: o.passingScorePct ?? null,
        showAnswersAtEnd: o.showAnswersAtEnd,
        autoReassignOnFail: o.autoReassignOnFail,
        maxAttempts: o.maxAttempts ?? null,
        createdBy: o.createdBy,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    // Ensure every orientation has a (possibly empty) link list.
    for (const o of orientations) if (!links.has(o.orientationId)) links.set(o.orientationId, []);

    return { orientations, steps, links, usage };
  }, [orientationRows, stepRows, linkRows, actionRows, checkRows, checkKeyRows]);

  // `checksReady` is reported apart from `isReady`: the two check views live in
  // their own subscription batch (they only exist once the checks republish has
  // landed on this org), so a caller that bulk-replaces a step's checks must
  // wait for it, while the rest of the editor stays usable without it.
  return {
    model,
    isReady: oReady && sReady && lReady && aReady && cReady && kReady,
    checksReady: cReady && kReady,
    // The orientation list alone (#243): decides "loading/reconnecting" vs
    // "no orientations yet" without waiting on the check views, which do not
    // exist on an org that has not had the checks republish.
    orientationsReady: oReady,
    // The script-action view alone: saveStop may only write an EMPTY beat list
    // (clearing a stop) once the stop's real actions have been read.
    actionsReady: aReady,
  };
}
